import { FunctionComponent, useCallback, useState, memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorModal } from '@selfserve/react-ui';

export interface ModalSettings {
  title?: string;
  text?: string;
  url?: string;
  withDissmiss?: boolean;
  dismissBtnText?: string;
  confirmBtnText?: string;
  dismissBtnId?: string;
  confirmBtnId?: string;
  preventActionsOnClose?: boolean;
  onDismissBtnClick?: () => void;
  onConfirmBtnClick?: () => void;
  callback?: () => void;
}

export interface IWrappedComponentProps {
  openErrorModal: (settings?: ModalSettings) => void;
}

export function withErrorHandling<T>(
  WrappedComponent: FunctionComponent<IWrappedComponentProps & T>
) {
  const Component = (props: T) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [callback, setCallback] = useState<(() => void) | null>(null);
    const [errorTitle, setErrorTitle] = useState('');
    const [errorText, setErrorText] = useState('');
    const [redirectUrl, setRedirectUrl] = useState('');
    const [dismissBtnText, setDissmissBtnText] = useState('');
    const [confirmBtnText, setConfirmBtnText] = useState('');
    const [dismissBtnId, setDismissBtnId] = useState('');
    const [confirmBtnId, setConfirmBtnId] = useState('');
    const [onDismissBtnClick, setOnDismissBtnClick] = useState<
      (() => void) | null
    >(null);
    const [onConfirmBtnClick, setOnConfirmBtnClick] = useState<
      (() => void) | null
    >(null);
    const [withDissmiss, setWithDissmiss] = useState(false);
    const [preventActionsOnClose, setPreventActionsOnClose] = useState(false);
    const MemoizedWrappedComponent = useMemo(() => memo(WrappedComponent), []);

    const handleCloseActions = useCallback(() => {
      if (typeof callback === 'function') callback();
      if (redirectUrl) {
        if (redirectUrl.includes('http')) {
          window.location.href = redirectUrl;
        } else {
          navigate(redirectUrl);
        }
      }
    }, [navigate, redirectUrl, callback]);

    const openErrorModal = useCallback((settings?: ModalSettings) => {
      const {
        title,
        text,
        url,
        withDissmiss,
        dismissBtnText,
        confirmBtnText,
        dismissBtnId,
        confirmBtnId,
        onDismissBtnClick,
        onConfirmBtnClick,
        callback,
        preventActionsOnClose,
      } = settings || {};
      setCallback(() => callback || null);
      setErrorTitle(title || '');
      setErrorText(text || '');
      setRedirectUrl(url || '');
      setWithDissmiss(!!withDissmiss);
      setDissmissBtnText(dismissBtnText || t('common.dismiss'));
      setConfirmBtnText(confirmBtnText || t('common.ok'));
      setConfirmBtnId(confirmBtnId || '');
      setDismissBtnId(dismissBtnId || '');
      setOnDismissBtnClick(() => onDismissBtnClick || null);
      setOnConfirmBtnClick(() => onConfirmBtnClick || null);
      setPreventActionsOnClose(!!preventActionsOnClose);
      setOpen(true);
    }, []);

    const modalDissmissHandle = useCallback(() => {
      setOpen(false);
      if (!preventActionsOnClose) handleCloseActions();
      if (onDismissBtnClick) onDismissBtnClick();
    }, [handleCloseActions, preventActionsOnClose, onDismissBtnClick]);

    const modalConfirmHandle = useCallback(() => {
      setOpen(false);
      handleCloseActions();
      if (onConfirmBtnClick) onConfirmBtnClick();
    }, [handleCloseActions, onConfirmBtnClick]);

    return (
      <>
        <MemoizedWrappedComponent openErrorModal={openErrorModal} {...props} />
        <ErrorModal
          open={!!open}
          errorTitle={errorTitle}
          errorText={errorText}
          onClose={modalConfirmHandle}
          onDissmiss={modalDissmissHandle}
          withDissmiss={!!withDissmiss}
          dismissBtnText={dismissBtnText}
          confirmBtnText={confirmBtnText}
          dismissBtnId={dismissBtnId}
          confirmBtnId={confirmBtnId}
        />
      </>
    );
  };

  return Component;
};
